// Authentication & user
// --------------------------------------------------------
import api from '@/lib/api'
import {Endpoints, RoleWeights, Roles} from '@/lib/definitions'
import * as Sentry from '@sentry/vue'
import Vue from 'vue'

const AUTHOR_ALLOWED_TIMESTAMP = 60 * 60 * 1000 // 1 hour

const state = {
  userIsLogged: false,
  user: {
    _id: null,
    email: null,
    username: null,
    picture: null,
    emailVerified: null
  },
  landscapes: [
    {
      _id: null,
      account: null,
      admin: null,
      name: null,
      slug: null,
      invitedAt: null,
      joinedAt: null,
      favorite: false,
      _name: null
    }
  ],
  invitedLandscapes: [
    {
      _id: null,
      account: null,
      admin: null,
      invitedAt: null,
      invitedBy: null,
      name: null,
      description: null,
      slug: null
    }
  ],
  startPage: null,
  landscapesPending: null,
  tempLandscapeInvitation: null,
  whatsNew: null
}

const getters = {
  userIsLogged: state => state.userIsLogged,
  userData: state => state.user,
  userId: state => state.user._id,
  username: state => state.user.username,
  picture: state => state.user.picture,
  emailVerified: state => state.user.emailVerified,
  email: state => state.user.email,
  landscapes: state => state.landscapes,
  startPage: state => state.startPage,
  isFavorite: state => slug => {
    const landscape = state.landscapes.find(l => l.slug === slug)
    if (landscape) return !!landscape.favorite
  },
  invitedLandscapes: state => state.invitedLandscapes,
  landscapesPending: state => state.landscapesPending,
  tempLandscapeInvitation: state => state.tempLandscapeInvitation,
  inAuthorWindowPermission: state => obj => {
    if (obj.author !== state.user._id) return false
    const createdAt = Date.parse(obj.createdAt)
    return Date.now() < createdAt + AUTHOR_ALLOWED_TIMESTAMP // author in allowed edit period
  },
  canCreateLandscape: state => state.landscapesPending > 0,
  newNews: state => {
    // TODO: Read news from backend
    const lastNewDate = 1646226737317 // manual Date.now() when a new is published !?
    return new Date(state.whatsNew) < new Date(lastNewDate)
  },
  startingLandscape: state => {
    if (state.startPage === 'home') {
      if (Array.isArray(state.landscapes) && state.landscapes.length > 0) {
        return state.landscapes[0].slug
      }
    } else {
      return state.startPage
    }
  },
  hasAdminRightsSlug: state => slug => {
    if (Array.isArray(state.landscapes)) {
      const ld = state.landscapes.find(l => l.slug === slug)
      return (
        ld &&
        ld.currentUser &&
        (ld.currentUser.role === Roles.ADMIN ||
          ld.currentUser.role === Roles.OWNER)
      )
    }
  }
}

const mutations = {
  updateMentionCount(state, {landscapeId, diff, set}) {
    const ld = state.landscapes.find(ld => ld._id === landscapeId)
    if (ld) {
      const current = ld.mentions || 0
      ld.mentions = diff ? Math.max(0, current + diff) : set
    }
  },
  logUserIn(state) {
    state.userIsLogged = true
  },
  setUserData(
    state,
    {
      _id = state.user._id,
      email = state.user.email,
      username = state.user.username,
      picture = state.user.picture,
      emailVerified = state.user.emailVerified
    }
  ) {
    state.user = {_id, email, username, picture, emailVerified}
  },
  setLandscapes(state, {landscapes, invitedLandscapes}) {
    state.landscapes = landscapes.map(ld => {
      // sorting props
      ld._name = ld.name.toLowerCase()
      ld.favorite = !!ld.favorite
      ld.currentUser._role = RoleWeights[ld.currentUser.role]
      return ld
    })
    state.invitedLandscapes = invitedLandscapes
  },
  setLandscapesPending(state, landscapesPending) {
    state.landscapesPending = landscapesPending
  },
  setInvitedLandscapes(state, invitedLandscapes) {
    state.invitedLandscapes = invitedLandscapes
  },
  setTempLandscapeInvitation(state, landscape) {
    state.tempLandscapeInvitation = landscape
  },
  removeTempLandscapeInvitation(state) {
    state.tempLandscapeInvitation = null
  },
  addNewLandscape(state, {_id, account, name, slug, currentUser}) {
    state.landscapes.push({_id, account, name, slug, currentUser})
    state.landscapesPending--
  },
  removeLandscapeFromList(state, landscapeId) {
    const index = state.landscapes.findIndex(l => l._id === landscapeId)
    state.landscapes.splice(index, 1)
  },
  setStartPage(state, startPage) {
    state.startPage = startPage
  },
  toggleLandscapeFavorite(state, slug) {
    const landscape = state.landscapes.find(l => l.slug === slug)
    if (landscape) {
      Vue.set(landscape, 'favorite', !landscape.favorite)
    }
  },
  setWhatsNew(state, date) {
    state.whatsNew = date
  },
  cleanState(state) {
    state.userIsLogged = false
    state.user._id = null
    state.user.email = null
    state.user.username = null
    state.user.picture = null
    state.user.emailVerified = null
  },
  updateLandscapeInList(state, landscape) {
    const editedLandscape = state.landscapes.find(
      ld => ld._id === landscape._id
    )
    if (state.startPage === editedLandscape.slug) {
      state.startPage = landscape.slug
    }
    editedLandscape.slug = landscape.slug
    editedLandscape.name = landscape.name
  }
}

const actions = {
  updateLandscapeListMentions(context, dic) {
    Object.keys(dic).forEach(landscapeId => {
      context.commit('updateMentionCount', {landscapeId, set: dic[landscapeId]})
    })
  },
  setLandscapes(context, payload) {
    context.commit('setLandscapes', payload)
    context.commit(
      'setLandscapesPending',
      payload.accessControl.LANDSCAPES_PENDING
    )
  },
  login(context) {
    return api.call('get', Endpoints.LOGIN).then(data => {
      // console.log('Login ok => ', data)
      context.commit('logUserIn')
      context.commit('setUserData', data.user)
      context.commit('setStartPage', data.startPage)
      if (data.preferences) {
        context.commit('preferences/save', data.preferences)
      }
      context.commit('setLandscapes', data)
      context.commit(
        'setLandscapesPending',
        data.accessControl.LANDSCAPES_PENDING
      )
      context.commit('setWhatsNew', data.whatsNew)
      context.dispatch('setPlan', data.plan)
      if (process.env.NODE_ENV === 'production') {
        Vue.$ga.set('userId', data.user._id)
        Sentry.setUser({
          id: data.user._id,
          email: data.user.email
        })
      }
    })
  },
  logout(context) {
    if (process.env.NODE_ENV === 'production') {
      Sentry.setUser(null)
    }
    context.commit('cleanState')
  },
  editProfile(context, payload) {
    return api.call('patch', Endpoints.PROFILE, payload).then(data => {
      context.commit('setUserData', {
        username: data.username,
        picture: data.picture
      })
      return data
    })
  },
  toggleFavorite(context, payload) {
    return api
      .call('patch', '/users/favoriteLandscape/', payload)
      .then(() =>
        context.commit('toggleLandscapeFavorite', payload.landscapeSlug)
      )
  },
  unsubscribe(context, email) {
    return api.call('post', '/users/unsubscribe/', {email})
  },
  leaveLandscape(context, landscapeId) {
    return api
      .call('post', `users/landscapes/${landscapeId}/leave`)
      .then(data => context.commit('setLandscapes', data))
  },
  acceptInvitation(context, landscapeId) {
    return api
      .call('post', `users/invitedLandscapes/${landscapeId}/accept`)
      .then(data => {
        context.commit('setLandscapes', data)
      })
  },
  rejectInvitation(context, landscapeId) {
    return api
      .call('post', `users/invitedLandscapes/${landscapeId}/reject`)
      .then(loginObject => {
        context.commit('setInvitedLandscapes', loginObject.invitedLandscapes)
      })
  },
  feedback(context, payload) {
    return api.call('post', '/feedback', payload)
  },
  whatsNew(context) {
    context.commit('setWhatsNew', Date.now())
    return api.call('post', '/users/whatsnew')
  },
  loadHome() {
    return api.call('get', `users/home`)
  },
  setStartPage(context, slug) {
    return api
      .call('patch', '/users/startPage/', {slug})
      .then(() => context.commit('setStartPage', slug))
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
